import { useState } from "react";
import { BookOpen, Inbox, Info, Plus, TriangleAlert } from "lucide-react";
import { Banner } from "@/components/ds/Banner";
import { Button } from "@/components/ds/Button";
import { Card } from "@/components/ds/Card";
import { Chip, type ChipTone } from "@/components/ds/Chip";
import { DataTable, type DataTableColumn } from "@/components/ds/DataTable";
import { EmptyState } from "@/components/ds/EmptyState";
import { FilterMenu } from "@/components/ds/FilterMenu";
import { Kpi } from "@/components/ds/Kpi";
import { Meter } from "@/components/ds/Meter";
import { NumberBox } from "@/components/ds/NumberBox";
import { PageHeader } from "@/components/ds/PageHeader";
import { Segmented } from "@/components/ds/Segmented";
import { SidePanel } from "@/components/ds/SidePanel";
import { Tabs, TabsPanel } from "@/components/ds/Tabs";
import { Toolbar } from "@/components/ds/Toolbar";

/** Literal class names so Tailwind generates every swatch. */
const SWATCHES: Array<{ token: string; className: string }> = [
  { token: "--ds-bg", className: "bg-ds-bg" },
  { token: "--ds-panel", className: "bg-ds-panel" },
  { token: "--ds-sunk", className: "bg-ds-sunk" },
  { token: "--ds-ink", className: "bg-ds-ink" },
  { token: "--ds-ink2", className: "bg-ds-ink2" },
  { token: "--ds-ink3", className: "bg-ds-ink3" },
  { token: "--ds-line", className: "bg-ds-line" },
  { token: "--ds-line2", className: "bg-ds-line2" },
  { token: "--ds-nav", className: "bg-ds-nav" },
  { token: "--ds-nav2", className: "bg-ds-nav2" },
  { token: "--ds-navink", className: "bg-ds-navink" },
  { token: "--ds-navhd", className: "bg-ds-navhd" },
  { token: "--ds-brand", className: "bg-ds-brand" },
  { token: "--ds-accent", className: "bg-ds-accent" },
  { token: "--ds-accent-ink", className: "bg-ds-accent-ink" },
  { token: "--ds-onaccent", className: "bg-ds-onaccent" },
  { token: "--ds-pink", className: "bg-ds-pink" },
  { token: "--ds-ok", className: "bg-ds-ok" },
  { token: "--ds-ok-tint", className: "bg-ds-ok-tint" },
  { token: "--ds-warn", className: "bg-ds-warn" },
  { token: "--ds-warn-tint", className: "bg-ds-warn-tint" },
  { token: "--ds-err", className: "bg-ds-err" },
  { token: "--ds-err-tint", className: "bg-ds-err-tint" },
  { token: "--ds-info-tint", className: "bg-ds-info-tint" },
];

const CHIP_TONES: ChipTone[] = ["published", "waiting", "draft", "info", "ok", "warn", "err", "neutral"];

interface SampleArticle {
  id: string;
  title: string;
  topic: string;
  status: "Published" | "Update waiting" | "Draft";
  helpful: number;
}

const ARTICLES: SampleArticle[] = [
  { id: "1", title: "Why aren't my steps counting?", topic: "Wellness Hub", status: "Published", helpful: 82 },
  { id: "2", title: "How do I redeem a perk?", topic: "Perks", status: "Update waiting", helpful: 64 },
  { id: "3", title: "Changing your notification choices", topic: "Settings", status: "Draft", helpful: 0 },
];

const STATUS_TONE: Record<SampleArticle["status"], ChipTone> = {
  Published: "published",
  "Update waiting": "waiting",
  Draft: "draft",
};

const COLUMNS: DataTableColumn<SampleArticle>[] = [
  { id: "title", header: "Article", cell: (a) => <span className="font-semibold text-ds-ink">{a.title}</span> },
  { id: "topic", header: "Topic", cell: (a) => a.topic },
  { id: "status", header: "Status", cell: (a) => <Chip tone={STATUS_TONE[a.status]} dot>{a.status}</Chip> },
  { id: "helpful", header: "Helpful", cell: (a) => <Meter value={a.helpful} label={`${a.title} helpful`} />, align: "right", width: 120 },
];

interface ShowcaseBlockProps {
  title: string;
  children: React.ReactNode;
}

function ShowcaseBlock({ title, children }: ShowcaseBlockProps) {
  return (
    <Card className="p-4">
      <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-ds-ink3">{title}</h3>
      {children}
    </Card>
  );
}

/**
 * "New CMS design system" section of the admin-only /design-system page: every C-CMS-DS
 * colour token and primitive, in the current theme (the audit's light + dark evidence).
 */
export function DsShowcase() {
  const [phone, setPhone] = useState("ios");
  const [stars, setStars] = useState("all");
  const [search, setSearch] = useState("");
  const [weight, setWeight] = useState("40");
  const [opened, setOpened] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  return (
    <section aria-labelledby="ds-showcase-heading" className="rounded-ds-lg bg-ds-bg p-4 font-ds text-sm text-ds-ink sm:p-6">
      <PageHeader
        as="h2"
        title={<span id="ds-showcase-heading">New CMS design system</span>}
        subtitle="C-CMS-DS tokens (raw HSL triplets) and the components/ds primitives. Only new screens use them."
        actions={<Button icon={<Plus className="h-4 w-4" aria-hidden="true" />}>Primary action</Button>}
      />

      <div className="grid gap-4">
        <ShowcaseBlock title="Colours">
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {SWATCHES.map((swatch) => (
              <li key={swatch.token} className="flex flex-col gap-1">
                <span className={`block h-9 rounded-lg border border-ds-line ${swatch.className}`} />
                <code className="font-ds-mono text-[11px] text-ds-ink2">{swatch.token}</code>
              </li>
            ))}
          </ul>
        </ShowcaseBlock>

        <ShowcaseBlock title="Typography">
          <p className="text-[22px] font-bold">Geist 22 / 700 — page title</p>
          <p className="text-[13.5px] text-ds-ink2">Geist 13.5 / 400 — subtitle and body</p>
          <p className="font-ds-mono text-[12.5px] font-medium">Geist Mono 500 — 0123456789</p>
        </ShowcaseBlock>

        <ShowcaseBlock title="Buttons">
          <div className="flex flex-wrap items-center gap-2">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button size="sm" variant="secondary">
              Small
            </Button>
            <Button loading>Saving</Button>
          </div>
        </ShowcaseBlock>

        <ShowcaseBlock title="Chips">
          <div className="flex flex-wrap gap-2">
            {CHIP_TONES.map((tone) => (
              <Chip key={tone} tone={tone} dot={tone === "published" || tone === "waiting"}>
                {tone}
              </Chip>
            ))}
          </div>
        </ShowcaseBlock>

        <ShowcaseBlock title="Tabs">
          <Tabs
            label="Sample sections"
            defaultValue="list"
            items={[
              { value: "list", label: "Articles", count: 34 },
              { value: "wait", label: "Updates waiting", count: 3 },
              { value: "topics", label: "Topics", count: 7 },
              { value: "insights", label: "Insights" },
            ]}
          >
            <TabsPanel value="list">Articles panel</TabsPanel>
            <TabsPanel value="wait">Updates waiting panel</TabsPanel>
            <TabsPanel value="topics">Topics panel</TabsPanel>
            <TabsPanel value="insights">Insights panel</TabsPanel>
          </Tabs>
        </ShowcaseBlock>

        <ShowcaseBlock title="Segmented">
          <Segmented
            label="Phone"
            value={phone}
            onChange={setPhone}
            options={[
              { value: "ios", label: "iPhone" },
              { value: "android", label: "Android" },
            ]}
          />
        </ShowcaseBlock>

        <ShowcaseBlock title="Toolbar and filters">
          <Card className="shadow-none">
            <Toolbar
              search={{ value: search, onChange: setSearch, placeholder: "Search articles", label: "Search articles" }}
              filters={
                <FilterMenu
                  label="Stars"
                  value={stars}
                  onChange={setStars}
                  options={[
                    { value: "all", label: "All" },
                    { value: "5", label: "5 stars" },
                    { value: "1", label: "1 star" },
                  ]}
                />
              }
              count="128 messages"
            />
          </Card>
        </ShowcaseBlock>

        <ShowcaseBlock title="Data table">
          <Card className="shadow-none">
            <DataTable
              label="Sample articles"
              columns={COLUMNS}
              rows={ARTICLES}
              getRowId={(a) => a.id}
              onRowClick={(a) => setOpened(a.title)}
            />
          </Card>
          <p className="mt-2 text-xs text-ds-ink3">{opened ? `Opened: ${opened}` : "Click or press Enter on a row."}</p>
          <Card className="mt-3 shadow-none">
            <DataTable label="Loading sample" columns={COLUMNS} rows={[]} getRowId={(a) => a.id} isLoading skeletonRows={2} />
          </Card>
        </ShowcaseBlock>

        <ShowcaseBlock title="Empty state">
          <EmptyState icon={Inbox} title="No articles yet" text="Import the help content to fill this list." action={{ label: "Import articles", onClick: () => setOpened("Import articles") }} />
        </ShowcaseBlock>

        <ShowcaseBlock title="KPIs and meters">
          <div className="grid gap-3 sm:grid-cols-3">
            <Kpi label="Feedback (30 days)" value="128" note="+12 vs last month" />
            <Kpi label="Average rating" value="4.3" note="+0.2" />
            <Kpi label="Without a rating" value="17" note="13%" noteTone="muted" />
          </div>
          <div className="mt-3 flex flex-wrap gap-4">
            <Meter value={82} label="Helpful sample" />
            <Meter value={35} label="Read sample" />
          </div>
        </ShowcaseBlock>

        <ShowcaseBlock title="Banners">
          <Banner tone="warn" icon={TriangleAlert} action={<Button variant="secondary" size="sm">Review</Button>}>
            <b>3 updates waiting</b> — newer text arrived from the import.
          </Banner>
          <Banner tone="info" icon={Info}>
            Changes apply at the next nightly update.
          </Banner>
          <Banner tone="ok">Saved.</Banner>
          <Banner tone="err">Could not save — try again.</Banner>
        </ShowcaseBlock>

        <ShowcaseBlock title="Number boxes">
          <p className="leading-loose text-ds-ink2">
            Challenges <NumberBox label="Challenges weight" value={weight} onChange={setWeight} suffix="%" /> · view only{" "}
            <NumberBox label="Social weight" value={15} suffix="%" readOnly />
          </p>
        </ShowcaseBlock>

        <ShowcaseBlock title="Side panel">
          <Button variant="secondary" icon={<BookOpen className="h-4 w-4" aria-hidden="true" />} onClick={() => setPanelOpen(true)}>
            Open side panel
          </Button>
          <SidePanel
            open={panelOpen}
            onOpenChange={setPanelOpen}
            title="Side panel"
            footer={<Button onClick={() => setPanelOpen(false)}>Done</Button>}
          >
            A ds-styled sheet: title, close, scrolling body and an optional footer.
          </SidePanel>
        </ShowcaseBlock>
      </div>
    </section>
  );
}
